/**
 * 컬러 팔레트 키 해석 유틸 (stencil `utils/color` 포팅).
 *
 * 컴포넌트의 `color` prop 은 팔레트 키(`grey_65`, `red_95` …)와
 * 임의의 CSS 색상 값(`#FF0000`, `rgb(...)`, `var(--foo)` …)을 모두 받는다.
 * 팔레트 키가 들어오면 대응하는 `var(--color-*)` 로 변환하고,
 * 그 외의 값은 그대로 통과시킨다.
 *
 * 팔레트 원본은 theme.css 의 `--color-*` 프리미티브(토큰 자동 생성) 다.
 * 키 목록은 그와 1:1 로 대응하며, 값은 var() 참조로 단일 소스를 유지한다.
 */

/** theme.css 의 `--color-*` 프리미티브와 1:1 대응하는 팔레트 키 */
export const COLOR_KEYS = [
  'white',
  'black',
  'grey_10',
  'grey_20',
  'grey_25',
  'grey_30',
  'grey_35',
  'grey_45',
  'grey_50',
  'grey_55',
  'grey_60',
  'grey_65',
  'grey_70',
  'grey_80',
  'grey_85',
  'grey_90',
  'grey_95',
  'grey_05',
  'red_15',
  'red_20',
  'red_30',
  'red_45',
  'red_60',
  'red_70',
  'red_75',
  'red_80',
  'red_85',
  'red_90',
  'red_95',
  'red_99',
  'orange_10',
  'orange_20',
  'orange_35',
  'orange_45',
  'orange_55',
  'orange_60',
  'orange_65',
  'orange_75',
  'orange_85',
  'orange_90',
  'orange_95',
  'orange_99',
  'yellow_10',
  'yellow_20',
  'yellow_25',
  'yellow_30',
  'yellow_40',
  'yellow_45',
  'yellow_50',
  'yellow_60',
  'yellow_70',
  'yellow_80',
  'yellow_90',
  'yellow_95',
  'olive_10',
  'olive_15',
  'olive_20',
  'olive_30',
  'olive_45',
  'olive_55',
  'olive_65',
  'olive_70',
  'olive_80',
  'olive_90',
  'olive_95',
  'olive_05',
  'green_15',
  'green_25',
  'green_45',
  'green_55',
  'green_65',
  'green_70',
  'green_75',
  'green_80',
  'green_85',
  'green_90',
  'green_95',
  'green_99',
  'steelblue_10',
  'steelblue_25',
  'steelblue_45',
  'steelblue_60',
  'steelblue_65',
  'steelblue_70',
  'steelblue_75',
  'steelblue_80',
  'steelblue_85',
  'steelblue_90',
  'steelblue_95',
  'steelblue_99',
  'oceanblue_15',
  'oceanblue_25',
  'oceanblue_50',
  'oceanblue_60',
  'oceanblue_65',
  'oceanblue_70',
  'oceanblue_75',
  'oceanblue_80',
  'oceanblue_85',
  'oceanblue_90',
  'oceanblue_95',
  'oceanblue_99',
  'brilliantblue_10',
  'brilliantblue_20',
  'brilliantblue_25',
  'brilliantblue_40',
  'brilliantblue_50',
  'brilliantblue_60',
  'brilliantblue_70',
  'brilliantblue_75',
  'brilliantblue_80',
  'brilliantblue_85',
  'brilliantblue_90',
  'brilliantblue_95',
  'brilliantblue_99',
  'brilliantblue_05',
] as const;

/** 팔레트 키 유니온 (`'grey_65' | 'red_95' | ...`) */
export type SColorKey = (typeof COLOR_KEYS)[number];

/**
 * `color` prop 타입. 팔레트 키 자동완성을 제공하면서 임의 CSS 색상 문자열도 허용한다.
 * `(string & {})` 는 유니온 리터럴 자동완성을 유지하기 위한 관용 표현이다.
 */
export type SColor = SColorKey | (string & {});

const KEY_SET = new Set<string>(COLOR_KEYS);

/** 문자열이 팔레트 키인지 판별하는 타입 가드 */
export function isColorKey(input: string): input is SColorKey {
  return KEY_SET.has(input);
}

/**
 * 팔레트 키면 대응하는 `var(--color-*)` 로 변환하고, 그 외 값은 원문 그대로 반환한다.
 * 값이 비어 있으면 `fallback`(기본 `undefined`) 을 반환한다.
 * (SIcon 처럼 미지정 시 currentColor 를 상속해야 하는 곳을 위해 fallback 기본값은 undefined)
 */
export function resolveColor(
  input: string | undefined | null,
  fallback?: string,
): string | undefined {
  if (input === null || input === undefined || input === '') return fallback;
  return isColorKey(input) ? `var(--color-${input.replace(/_/g, '-')})` : input;
}
